import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Building2, Upload, X, Loader2, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';


const CompanySettings = () => {
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [showResultsToCandidates, setShowResultsToCandidates] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingResults, setSavingResults] = useState(false);
  const { toast } = useToast();
  const { currentCompany } = useCompany();
  const { companyRole, isSuperAdmin } = useAuth();

  // Only owners and admins can manage results visibility
  const canManageResultsVisibility = companyRole === 'owner' || companyRole === 'admin' || isSuperAdmin();

  useEffect(() => {
    if (currentCompany) {
      fetchCompanyData();
    }
  }, [currentCompany]);

  const fetchCompanyData = async () => {
    if (!currentCompany?.company_id) return;

    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name, logo_url, show_results_to_candidates')
        .eq('id', currentCompany.company_id)
        .single();

      if (error) throw error;

      setCompanyName(data.name || '');
      setLogoUrl(data.logo_url || null);
      setShowResultsToCandidates(data.show_results_to_candidates ?? false);
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) return;
      if (!currentCompany?.company_id) {
        toast({
          title: "Error",
          description: "No company selected",
          variant: "destructive"
        });
        return;
      }

      const file = event.target.files[0];
      
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "File size must be less than 2MB",
          variant: "destructive"
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: "Error",
          description: "Please upload an image file",
          variant: "destructive"
        });
        return;
      }

      setUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${currentCompany.company_id}/logo.${fileExt}`;
      const filePath = `${fileName}`;

      if (logoUrl) {
        const oldPath = logoUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('company-logos').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: publicUrl })
        .eq('id', currentCompany.company_id);

      if (updateError) throw updateError;

      setLogoUrl(publicUrl);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Error",
        description: "Failed to upload logo",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentCompany?.company_id || !logoUrl) return;

    try {
      setUploading(true);

      const oldPath = logoUrl.split('/').slice(-2).join('/');
      await supabase.storage.from('company-logos').remove([oldPath]);

      const { error } = await supabase
        .from('companies')
        .update({ logo_url: null })
        .eq('id', currentCompany.company_id);

      if (error) throw error;

      setLogoUrl(null);
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({
        title: "Error",
        description: "Failed to remove logo",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCompanyName = async () => {
    if (!currentCompany?.company_id) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('companies')
        .update({ name: companyName })
        .eq('id', currentCompany.company_id);

      if (error) throw error;

    } catch (error) {
      console.error('Error updating company name:', error);
      toast({
        title: "Error",
        description: "Failed to update company name",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShowResults = async (checked: boolean) => {
    if (!currentCompany?.company_id) return;

    try {
      setSavingResults(true);
      setShowResultsToCandidates(checked);

      const { error } = await supabase
        .from('companies')
        .update({ show_results_to_candidates: checked })
        .eq('id', currentCompany.company_id);

      if (error) throw error;
      toast({
        title: "Settings Updated",
        description: checked 
          ? "Candidates will now see their assessment results" 
          : "Candidates will only see a confirmation message",
      });
    } catch (error) {
      console.error('Error updating show results setting:', error);
      setShowResultsToCandidates(!checked); // Revert on error
      toast({
        title: "Error",
        description: "Failed to update setting",
        variant: "destructive"
      });
    } finally {
      setSavingResults(false);
    }
  };

  if (!currentCompany) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Company</h2>
          <p className="text-sm text-muted-foreground mt-1">
            No company selected
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Company</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your company profile and branding
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company Profile
          </CardTitle>
          <CardDescription>
            Update your company information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Company Logo */}
          <div className="space-y-4">
            <Label>Company Logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                {logoUrl ? (
                  <div className="relative group">
                    <img 
                      src={logoUrl} 
                      alt="Company logo" 
                      className="h-20 w-20 rounded-lg object-cover border-2 border-border"
                    />
                    <button
                      onClick={handleRemoveLogo}
                      disabled={uploading}
                      className="absolute -top-2 -right-2 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-20 w-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploading}
                  className="hidden"
                  id="logo-upload"
                />
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <Button type="button" disabled={uploading} asChild variant="outline">
                    <span>
                      {uploading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                    </span>
                  </Button>
                </Label>
                <p className="text-xs text-muted-foreground mt-2">
                  PNG, JPG, WEBP or SVG. Max 2MB.
                </p>
              </div>
            </div>
          </div>

          {/* Company Name */}
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name</Label>
            <div className="flex gap-2">
              <Input
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={saving}
                className="flex-1"
              />
              <Button 
                onClick={handleSaveCompanyName} 
                disabled={saving || companyName === currentCompany.company_name}
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assessment Results Visibility - Only visible to owners and admins */}
      {canManageResultsVisibility && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Assessment Results
            </CardTitle>
            <CardDescription>
              Control what candidates see after completing assessments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="show-results">Show Results to Candidates</Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, candidates will see their behavioral profile, graphics, and AI insights after completing assessments. 
                  When disabled, they'll only see a confirmation that their assessment was submitted.
                </p>
              </div>
              <Switch
                id="show-results"
                checked={showResultsToCandidates}
                onCheckedChange={handleToggleShowResults}
                disabled={savingResults}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompanySettings;